import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layers, ExternalLink, FileCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FrameworkMapping = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      const response = await axios.get(`${API}/framework-mapping`);
      setMappings(response.data);
    } catch (error) {
      console.error('Error fetching framework mappings:', error);
    } finally {
      setLoading(false);
    }
  };

  const frameworks = [
    { id: 'gri', name: 'GRI Standards', description: 'Global Reporting Initiative - Universal sustainability reporting standards', color: '#1A362D' },
    { id: 'brsr', name: 'BRSR', description: 'Business Responsibility and Sustainability Reporting - SEBI mandate for Indian listed companies', color: '#7898A5' },
    { id: 'tcfd', name: 'TCFD', description: 'Task Force on Climate-related Financial Disclosures - Climate risk framework', color: '#C85A32' },
    { id: 'ifrs', name: 'IFRS S2', description: 'International Sustainability Standards Board - Climate-related disclosures', color: '#8B9D8F' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A362D]"></div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="framework-mapping-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-[#111814] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Framework Alignment
        </h1>
        <p className="text-base text-[#4B5563] leading-relaxed">
          See how your ESG data maps to major reporting frameworks and standards
        </p>
      </div>

      {/* Framework Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {frameworks.map((fw) => (
          <Card key={fw.id} className="border-[#E5E1D8] hover:shadow-sm transition-all duration-200" data-testid={`framework-card-${fw.id}`}>
            <CardContent className="pt-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${fw.color}20` }}>
                <FileCheck className="w-5 h-5" style={{ color: fw.color }} />
              </div>
              <h3 className="font-semibold text-[#111814] mb-1">{fw.name}</h3>
              <p className="text-xs text-[#6B7280] leading-relaxed">{fw.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Mapping Table */}
      <Card className="border-[#E5E1D8] mb-6" data-testid="framework-mapping-table">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#1A362D]" />
            <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Data to Framework Mapping</CardTitle>
          </div>
          <CardDescription>
            Your ESG data automatically mapped to disclosure requirements across frameworks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E5E1D8]">
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Data Category</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Your Value</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#1A362D]">GRI</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#7898A5]">BRSR</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#C85A32]">TCFD</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#8B9D8F]">IFRS S2</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((mapping, index) => (
                  <tr key={index} className="border-b border-[#F2F0EB] hover:bg-[#F9F8F6] transition-colors">
                    <td className="py-3 px-4">
                      <span className="font-medium text-[#111814]">{mapping.data_category}</span>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className="bg-[#1A362D]/10 text-[#1A362D] border-0">{mapping.value}</Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-[#4B5563]">{mapping.gri_indicator}</td>
                    <td className="py-3 px-4 text-sm text-[#4B5563]">{mapping.brsr_principle}</td>
                    <td className="py-3 px-4 text-sm text-[#4B5563]">{mapping.tcfd_category}</td>
                    <td className="py-3 px-4 text-sm text-[#4B5563]">{mapping.ifrs_s2_topic}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Framework Details Tabs */}
      <Tabs defaultValue="gri" className="w-full" data-testid="framework-details-tabs">
        <TabsList className="grid w-full max-w-2xl grid-cols-4 mb-6">
          <TabsTrigger value="gri">GRI</TabsTrigger>
          <TabsTrigger value="brsr">BRSR</TabsTrigger>
          <TabsTrigger value="tcfd">TCFD</TabsTrigger>
          <TabsTrigger value="ifrs">IFRS S2</TabsTrigger>
        </TabsList>

        <TabsContent value="gri">
          <Card className="border-[#1A362D]/30">
            <CardHeader>
              <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>GRI Standards Alignment</CardTitle>
              <CardDescription>Global Reporting Initiative - Environmental Disclosures</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-[#F9F8F6] rounded-lg">
                  <h4 className="font-semibold text-[#111814] mb-2">GRI 302: Energy</h4>
                  <p className="text-sm text-[#4B5563]">302-1: Energy consumption within the organization • 302-3: Energy intensity • 302-4: Reduction of energy consumption</p>
                </div>
                <div className="p-4 bg-[#F9F8F6] rounded-lg">
                  <h4 className="font-semibold text-[#111814] mb-2">GRI 305: Emissions</h4>
                  <p className="text-sm text-[#4B5563]">305-1: Direct GHG emissions (Scope 1) • 305-2: Energy indirect GHG emissions (Scope 2) • 305-3: Other indirect GHG emissions (Scope 3) • 305-4: GHG emissions intensity • 305-5: Reduction of GHG emissions</p>
                </div>
                <div className="p-4 bg-[#F9F8F6] rounded-lg">
                  <h4 className="font-semibold text-[#111814] mb-2">GRI 303: Water</h4>
                  <p className="text-sm text-[#4B5563]">303-3: Water withdrawal • 303-5: Water consumption</p>
                </div>
                <div className="p-4 bg-[#F9F8F6] rounded-lg">
                  <h4 className="font-semibold text-[#111814] mb-2">GRI 306: Waste</h4>
                  <p className="text-sm text-[#4B5563]">306-3: Waste generated • 306-4: Waste diverted from disposal • 306-5: Waste directed to disposal</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="brsr">
          <Card className="border-[#7898A5]/30">
            <CardHeader>
              <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>BRSR Alignment</CardTitle>
              <CardDescription>Business Responsibility and Sustainability Reporting (SEBI)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-[#F9F8F6] rounded-lg">
                  <h4 className="font-semibold text-[#111814] mb-2">Principle 6: Environment</h4>
                  <p className="text-sm text-[#4B5563] mb-2">Businesses should respect and make efforts to protect and restore the environment</p>
                  <ul className="text-sm text-[#6B7280] list-disc list-inside space-y-1">
                    <li>Total energy consumption and intensity</li>
                    <li>Scope 1, 2, 3 GHG emissions</li>
                    <li>Water withdrawal and consumption</li>
                    <li>Waste generated and management</li>
                    <li>Environmental compliance</li>
                  </ul>
                </div>
                <div className="p-4 bg-[#7898A5]/10 rounded-lg border border-[#7898A5]/30">
                  <p className="text-sm text-[#4B5563]">
                    <span className="font-semibold">Note:</span> BRSR is mandatory for top 1000 listed companies by market capitalization in India. Your data is structured to meet Section C disclosures.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tcfd">
          <Card className="border-[#C85A32]/30">
            <CardHeader>
              <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>TCFD Alignment</CardTitle>
              <CardDescription>Task Force on Climate-related Financial Disclosures</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#F9F8F6] rounded-lg">
                  <h4 className="font-semibold text-[#111814] mb-2">Governance</h4>
                  <p className="text-sm text-[#4B5563]">Board oversight and management's role in climate-related risks</p>
                </div>
                <div className="p-4 bg-[#F9F8F6] rounded-lg">
                  <h4 className="font-semibold text-[#111814] mb-2">Strategy</h4>
                  <p className="text-sm text-[#4B5563]">Climate-related risks and opportunities, scenario analysis</p>
                </div>
                <div className="p-4 bg-[#F9F8F6] rounded-lg">
                  <h4 className="font-semibold text-[#111814] mb-2">Risk Management</h4>
                  <p className="text-sm text-[#4B5563]">Processes for identifying, assessing, and managing climate risks</p>
                </div>
                <div className="p-4 bg-[#C85A32]/10 rounded-lg border border-[#C85A32]/30">
                  <h4 className="font-semibold text-[#111814] mb-2">Metrics & Targets</h4>
                  <p className="text-sm text-[#4B5563]">GHG emissions (Scope 1, 2, 3), climate-related targets and performance</p>
                  <Badge className="mt-2 bg-[#C85A32] text-white">Your Data Maps Here</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ifrs">
          <Card className="border-[#8B9D8F]/30">
            <CardHeader>
              <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>IFRS S2 Alignment</CardTitle>
              <CardDescription>Climate-related Disclosures (ISSB Standards)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-[#F9F8F6] rounded-lg">
                  <h4 className="font-semibold text-[#111814] mb-2">Climate-related Metrics</h4>
                  <ul className="text-sm text-[#6B7280] list-disc list-inside space-y-1">
                    <li>Absolute gross GHG emissions (Scope 1, 2, 3)</li>
                    <li>GHG emissions intensity</li>
                    <li>Amount and percentage of assets/activities vulnerable to climate risks</li>
                    <li>Capital deployment toward climate-related opportunities</li>
                  </ul>
                </div>
                <div className="p-4 bg-[#F9F8F6] rounded-lg">
                  <h4 className="font-semibold text-[#111814] mb-2">Climate-related Targets</h4>
                  <ul className="text-sm text-[#6B7280] list-disc list-inside space-y-1">
                    <li>Quantitative and qualitative climate targets</li>
                    <li>Metrics used to assess progress</li>
                    <li>Comparison against third-party validated targets (e.g., SBTi)</li>
                  </ul>
                </div>
                <div className="p-4 bg-[#8B9D8F]/10 rounded-lg border border-[#8B9D8F]/30">
                  <p className="text-sm text-[#4B5563]">
                    <span className="font-semibold">Note:</span> IFRS S2 becomes effective for annual reporting periods beginning on or after January 1, 2024. Early adoption is encouraged.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FrameworkMapping;
